<template>
  <div class="bg-white rounded-sm m-2">
    <b-modal ref="certificationDetails" title="Certification Details" size="lg" hide-footer>
      <LegalDocumentCertificationDetails :tracking-number="detailsTrackingNumber" v-if="detailsTrackingNumber" />
    </b-modal>

    <!-- Filters & Search Header -->
    <b-row class="mt-2">
      <b-col cols="12">
        <b-row>
          <b-col cols="3">
            <b-form-group label="Search" label-for="search-input" class="ml-2">
              <b-form-input
                id="search-input"
                class="text-gray text-sm noFocus"
                style="padding:6px 5px;"
                v-model="search"
                type="text"
                placeholder="Search by Tracking No."
                v-on:keyup.enter="onSearchEnter">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group label="Filter Status" label-for="filter-status" class="ml-2">
              <b-form-select
                id="filter-status"
                v-model="filterStatus"
                v-on:change="fetch"
                class="form-select noFocus text-sm text-gray">
                <b-select-option :value="null">--Filter by Status--</b-select-option>
                <b-select-option value="DRAFT">Draft</b-select-option>
                <b-select-option value="WAITING">Waiting</b-select-option>
                <b-select-option value="SUCCESS">Success</b-select-option>
                <b-select-option value="FAILED">Failed</b-select-option>
                <b-select-option value="Pending">Submitted</b-select-option>
                <b-select-option value="Approved">Approved</b-select-option>
                <b-select-option value="Queried">Issues Raised</b-select-option>
                <b-select-option value="PAID">Paid</b-select-option>
                <b-select-option value="PAYMENT_FAILED">Payment Failed</b-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-row>
              <b-col class="mx-auto">
                <p class="paginate-details text-center text-xs font-bold float-right mt-4">
                  <b-form-select v-model="limit" class="w-full text-xs form-select border-0 paginate-details" v-on:change="fetch">
                    <b-select-option value="10">10</b-select-option>
                    <b-select-option value="20">20</b-select-option>
                    <b-select-option value="50">50</b-select-option>
                    <b-select-option value="100">100</b-select-option>
                  </b-form-select>
                  <span>{{((currentPage - 1) * limit) + 1}}</span> -
                  <span v-if="limit > certifications.length">{{pagination.totalElements}}</span>
                  <span v-else>{{currentPage * limit}}</span>
                  of {{pagination.totalElements}}
                </p>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <!-- Progress bar -->
        <div class="mx-auto">
          <b-progress value="100" v-if="loading" variant="#007ac5" height="0.2rem" animated></b-progress>
        </div>

        <!-- Table -->
        <b-row>
          <b-col cols="12">
            <table class="min-w-full leading-normal">
              <thead>
                <tr class="border-b border-t border-grey">
                  <th class="t-header">Tracking No.</th>
                  <th class="t-header">Document Type</th>
                  <th class="t-header" style="max-width: 250px;">Search Reference</th>
                  <th class="t-header">Applicant</th>
                  <th class="t-header">PRN</th>
                  <th class="t-header">Payment Mode</th>
                  <th class="t-header text-right">Amount</th>
                  <th class="t-header text-center">Status</th>
                  <th class="t-header text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr :class="[(idx % 2) ? rows.even : rows.odd]" v-for="(certification, idx) in certifications" :key="certification.id || idx">
                  <td class="t-body text-blue font-bold cursor-pointer hover:underline" @click="viewDetails(certification)">
                    {{ certification.trackingNumber }}
                  </td>
                  <td class="t-body font-bold">{{ cleanEntityType(certification.documentType || certification.type || '-') }}</td>
                  <td class="t-body text-break" style="max-width: 250px; word-break: break-word; white-space: normal;" :title="certification.search">
                    {{ certification.search || '-' }}
                  </td>
                  <td class="t-body">
                    <div class="font-bold text-capitalize">{{ applicantName(certification.applicant) }}</div>
                    <div class="text-xs text-gray" v-if="certification.applicant && certification.applicant.phoneNumber">
                      {{ certification.applicant.phoneNumber }}
                    </div>
                  </td>
                  <td class="t-body">{{ certification.prn || '-' }}</td>
                  <td class="t-body font-bold">
                    {{ certification.paymentMode ? certification.paymentMode.replace(/_/g, ' ') : "-" }}
                  </td>
                  <td class="t-body text-right">
                    {{ certification.amount ? Number(certification.amount).toLocaleString() : '-' }}
                  </td>
                  <td class="t-body text-center">
                    <p class="text-xs px-2 py-1 text-capitalize text-center" style="background: #f1f1f1">
                      <span :class="statusColorCode(certification.status ? certification.status.toUpperCase() : '')">
                        {{ statusDataInfo(certification.status ? certification.status.toUpperCase() : '') }}
                      </span>
                    </p>
                  </td>
                  <td class="t-body text-center">
                    <button
                      type="button"
                      class="bg-blue text-white px-2 py-1 rounded-sm text-xs"
                      @click="viewDetails(certification)"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
                <tr v-if="certifications.length === 0 && !loading">
                  <td colspan="9" class="bg-inertgrey text-center py-2">
                    <span class="text-sm">No results found</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Pagination -->
        <b-row class="mx-1 my-2" v-if="pagination.totalPages > 1">
          <b-col cols="1">
            <button @click="previous" :class="(currentPage > 1) ? 'paginate-btn' : 'inert-btn'">Previous</button>
          </b-col>
          <b-col cols="1">
            <b-row>
              <b-col cols="12">
                <p class="paginate-details text-center">
                  <b-badge class="bg-blue text-white">
                    {{currentPage}}/{{pagination.totalPages}}
                  </b-badge>
                </p>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="1">
            <button @click="next" :class="(currentPage < pagination.totalPages) ? 'paginate-btn' : 'inert-btn'">Next</button>
          </b-col>
          <b-col cols="7"></b-col>
          <b-col cols="2">
            <p class="paginate-details text-center text-xs font-bold">
              <span>{{((currentPage - 1) * limit) + 1}}</span> -
              <span v-if="limit > certifications.length">{{pagination.totalElements}}</span>
              <span v-else>{{currentPage * limit}}</span>
              of {{pagination.totalElements}}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { api } from "@/app/services/api.service";
import LegalDocumentCertificationDetails
  from "@/views/home/services/legal-documents/certifications/LegalDocumentCertificationDetails";

export default {
  name: "LegalDocumentCertifications",
  components: { LegalDocumentCertificationDetails },
  data() {
    return {
      loading: false,
      certifications: [],
      currentPage: 1,
      limit: 10,
      pagination: {
        totalElements: 0,
        totalPages: 0,
      },
      filters: {
        page: 1,
        perPage: 10,
        status: null,
        search: null,
      },
      search: null,
      filterStatus: null,
      detailsTrackingNumber: null,
      rows: {
        odd: "border-t border-grey hover:bg-header_grey",
        even: "border-t border-grey hover:bg-header_grey",
      },
    };
  },
  mounted() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.filters.page = this.currentPage;
      this.filters.perPage = this.limit;
      this.filters.status = this.filterStatus || "";
      if (this.search && this.search.trim() !== "") {
        this.filters.search = this.search.trim();
      } else {
        this.filters.search = null;
      }
      this.loading = true;
      this.fetchLegalDocumentCertifications(this.filters);
    },
    onSearchEnter() {
      this.currentPage = 1;
      this.fetch();
    },
    async fetchLegalDocumentCertifications(data) {
      let suffix = "?status=" + (data.status || "");
      suffix += "&page=" + data.page;
      suffix += "&perPage=" + data.perPage;
      if (data.search) {
        suffix += "&search=" + encodeURIComponent(data.search);
      }

      try {
        const response = await api().get("legal-documents/certification" + suffix);
        if (response) {
          this.loading = false;
          const responseData = response.data;
          this.certifications = responseData.content || [];
          this.pagination = {
            totalPages: responseData.totalPages || 0,
            totalElements: responseData.totalElements || 0,
          };
        }
      } catch (error) {
        this.loading = false;
        if (error.response && error.response.data && error.response.data.message) {
          this.warningToast("Warning", error.response.data.message);
        }
      }
    },
    next() {
      if (this.currentPage < this.pagination.totalPages) {
        this.currentPage++;
        this.fetch();
      }
    },
    previous() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetch();
      } else {
        this.warningToast("Observation", "No more data to show");
      }
    },
    applicantName(applicant) {
      if (!applicant) return "-";
      if (applicant.firstName && applicant.lastName) {
        return applicant.firstName + " " + applicant.lastName;
      }
      return applicant.firstName || applicant.lastName || "-";
    },
    statusDataInfo(status) {
      if (!status) return "";
      const value = status.toUpperCase();
      if (value === "APPROVED") return "Approved";
      if (value === "ASSIGNED") return "Being Processed";
      if (value === "QUERIED") return "Issues Raised";
      if (value === "ESCALATED") return "Being Processed";
      if (value === "PENDING") return "Submitted";
      if (value === "AWAITING_PAYMENT_CONFIRMATION") return "Confirming Payment";
      if (value === "FAILED" || value === "PAYMENT_FAILED" || value === "ERROR") return "Payment Failed";
      if (value === "PAYMENT_CANCELLED") return "Payment Cancelled";
      if (value === "REJECTED") return "Rejected";
      if (value === "DRAFT") return "Draft";
      if (value === "PAID") return "Paid";
      if (value === "WAITING") return "Waiting";
      if (value === "SUCCESS") return "Success";
      const lower = status.toLowerCase().replaceAll("_", " ");
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    },
    statusColorCode(status) {
      if (!status) return "text-gray";
      const value = status.toUpperCase();
      if (value === "APPROVED" || value === "PAID" || value === "SUCCESS") return "text-green font-bold";
      if (value === "ASSIGNED" || value === "ESCALATED") return "text-blue font-bold";
      if (value === "QUERIED") return "text-orange font-bold";
      if (value === "PENDING" || value === "DRAFT") return "text-gray font-bold";
      if (value === "AWAITING_PAYMENT_CONFIRMATION" || value === "WAITING") return "text-purple font-bold";
      if (value === "FAILED" || value === "PAYMENT_FAILED" || value === "REJECTED" || value === "ERROR") return "text-red font-bold";
      return "text-gray font-bold";
    },
    cleanEntityType(name) {
      if (!name) return "";
      const n = name.replaceAll("-", " ").replaceAll("_", " ");
      return n.charAt(0).toUpperCase() + n.slice(1);
    },
    viewDetails(certification) {
      if (certification && certification.trackingNumber) {
        this.detailsTrackingNumber = certification.trackingNumber;
        this.$refs.certificationDetails.show();
      }
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
      this.fetch();
    },
  },
};
</script>

<style scoped>
@import "../../../../../assets/styles/table.css";

.paginate-details {
  @apply px-3 py-2 bg-white my-2 text-sm text-gray;
}

.paginate-btn {
  @apply px-3 py-2 bg-white border border-white my-2 text-sm text-blue w-full;
}

.paginate-btn:hover {
  @apply border border-blue text-blue rounded-sm;
}

.inert-btn {
  @apply px-3 py-2 bg-white my-2 text-sm text-grey w-full;
}
</style>
